import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ScaleLinear, ScaleTime, select, Selection } from 'd3';
import { fromEvent, Subscription } from 'rxjs';
import {
  AxisInfo,
  ChartDimensions,
  ChartLayout,
  formatTick,
  InitializeChartUtility,
  XAxisDirection,
  YAxisDirection,
} from '@twaice-fe/frontend/shared/utilities';
import { BarChartData } from '@twaice-fe/shared/models';
import { SelectionSVGBarsGroup } from './models/bar-chart.models';
import { DatePipe } from '@angular/common';

// TODO - remove this component after HealthBarChartComponent is finished
@Component({
  selector: 'twaice-fe-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BarChartComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() selectedData: BarChartData;

  @Input() excludedData?: BarChartData;

  @Input() disabledData?: BarChartData;

  @Input() layout: ChartLayout;

  @Input() xAxisDirection: XAxisDirection;

  @Input() yAxisDirection: YAxisDirection;

  @Input() isXAxisInTime?: boolean;

  @Input() isYAxisInTime?: boolean;

  @Input() axisTopOffset?: number = 0;

  @Input() axisBottomOffset?: number = 0;

  @Input() axisRightOffset?: number = 0;

  @Input() axisLeftOffset?: number = 0;

  @Input() hasXAxisGrid?: boolean = true;

  @Input() hasYAxisGrid?: boolean = true;

  @Input() hasTooltip?: boolean = false;

  @Input() hasBarSelection?: boolean = false;

  @Input() hasResetFilterButton?: boolean = false;

  @Input() errorText?: string;

  // Chart dimensions
  @Input() dimensions: ChartDimensions;

  @Input() hasXAxisLineDomain?: boolean = true;

  @Input() hasYAxisLineDomain?: boolean = true;

  @ViewChild('barChart', { static: true }) barChart: ElementRef<HTMLDivElement>;

  @Output() selectBar: EventEmitter<[number, number]> = new EventEmitter();

  @Output() resetFilter: EventEmitter<void> = new EventEmitter();

  private chartContainer: HTMLDivElement;

  private chartWrapper: Selection<SVGGElement, unknown, null, undefined>;

  private chartContent: Selection<SVGGElement, unknown, null, undefined>;

  // Convertors from response value to chart value
  private xScale: ScaleTime<number, number> | ScaleLinear<number, number>;

  private yScale: ScaleTime<number, number> | ScaleLinear<number, number>;

  private windowResizeSubscription: Subscription;

  private bars: Selection<SVGGElement, unknown, null, undefined>;

  isLoading = true;

  constructor(
    private chartUtility: InitializeChartUtility,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.chartContainer = this.barChart.nativeElement;
  }

  ngAfterViewInit(): void {
    // setup resize event to keep the charts responsive
    this.windowResizeSubscription = fromEvent(window, 'resize').subscribe(() => {
      if (
        this.chartContainer &&
        this.chartContainer.clientWidth &&
        this.chartContainer.clientHeight &&
        this.selectedData &&
        this.layout
      ) {
        this.initChart();
        if (this.hasResetFilterButton) {
          this.onFilterReset();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.isLoading = true;

    if ((changes.selectedData || changes.excludedData || changes.disabledData) && !changes.layout) {
      this.clearChartData();
      this.initBars();
    } else {
      this.chartContainer = this.barChart.nativeElement;
      this.initChart();
    }
  }

  private clearChartData() {
    this.chartContent.selectAll('#bars').remove();
  }

  private clearChart() {
    select(this.chartContainer).selectAll('*').remove();
  }

  /**
   * Draw X, Y Axis and Grid
   * Add X, Y Labels and chart title
   */
  private drawGeneralLayout() {
    this.chartUtility.setXAxis();
    this.xScale = this.chartUtility.getXScale();
    const xAxis = this.chartUtility.getXAxis();

    this.chartUtility.setYAxis();
    this.yScale = this.chartUtility.getYScale();
    const yAxis = this.chartUtility.getYAxis();

    if (this.hasXAxisGrid) {
      this.chartUtility.setXAxisGrid();
      const xGridLines = this.chartUtility.getXAxisGrid();
      // We should append XAxisGrid before XAxis, so that axes not been overwritten
      this.chartContent.node().append(xGridLines.firstChild);
    }

    if (this.hasYAxisGrid) {
      this.chartUtility.setYAxisGrid();
      const yGridLines = this.chartUtility.getYAxisGrid();
      // We should append YAxisGrid before XAxis, so that axes not been overwritten
      this.chartContent.node().append(yGridLines.firstChild);
    }

    this.chartContent.node().append(xAxis.firstChild);

    this.chartContent.select('#x_axis').classed('hide-domain', !this.hasXAxisLineDomain);

    this.chartContent.node().append(yAxis.firstChild);

    this.chartContent.select('#y_axis').classed('hide-domain', !this.hasYAxisLineDomain);

    if (this.layout.title) {
      this.chartUtility.setChartTitle(this.layout.title, this.layout.yLabel != null);
      const chartTitle = this.chartUtility.getChartTitle();
      this.chartWrapper.node().append(chartTitle.firstChild);
    }

    if (this.layout.xLabel) {
      this.chartUtility.setXAxisLabel(this.layout.xLabel);
      const xAxisLabel = this.chartUtility.getXAxisLabel();
      this.chartWrapper.node().append(xAxisLabel.firstChild);
    }

    if (this.layout.yLabel) {
      this.chartUtility.setYAxisLabel(this.layout.yLabel);
      const yAxisLabel = this.chartUtility.getYAxisLabel();
      this.chartWrapper.node().append(yAxisLabel.firstChild);
    }
  }

  attachTooltipEventToBar(bar: Selection<SVGGElement, unknown, null, undefined>, values?: number[]) {
    if (bar && values) {
      bar
        .on('mouseover', (event) =>
          this.chartUtility.onTooltipMouseover(
            event.currentTarget,
            values.map((value) => (this.isXAxisInTime ? this.datePipe.transform(value, 'll') : value)).join('</span><br><span>')
          )
        )
        .on('mouseleave', (event) => this.chartUtility.onTooltipMouseleave(event.currentTarget));
    }
  }

  drawBar({
    barWrapper,
    yPosition,
    height,
    colors,
    yIndex,
  }: {
    barWrapper: Selection<SVGGElement, unknown, null, undefined>;
    yPosition: number;
    height: number;
    colors: string[];
    yIndex: number;
  }): Selection<SVGGElement, unknown, null, undefined> {
    return barWrapper
      .append('rect')
      .attr('class', colors[yIndex])
      .attr('data-test', 'color-' + (yIndex + 1))
      .attr('y', this.yScale(yPosition))
      .attr('height', this.dimensions.height - this.dimensions.axisOffsetBottom - this.yScale(height));
  }

  drawGroupBars(barWrapper: Selection<SVGGElement, unknown, null, undefined>, xIndex: number): SelectionSVGBarsGroup {
    let yPosition = 0;

    let selectedBar;

    let excludedBar;

    let disabledBar;

    if (this.selectedData && this.selectedData.y[xIndex]) {
      yPosition += this.selectedData.y[xIndex];
      selectedBar = this.drawBar({
        barWrapper,
        yPosition,
        height: this.selectedData.y[xIndex],
        colors: this.layout.chartColors,
        yIndex: 0,
      });
    }

    if (this.excludedData && this.excludedData.y[xIndex]) {
      yPosition += this.excludedData.y[xIndex];
      excludedBar = this.drawBar({
        barWrapper,
        yPosition,
        height: this.excludedData.y[xIndex],
        colors: this.layout.chartColors,
        yIndex: 1,
      });
    }

    if (this.disabledData && this.disabledData.y[xIndex]) {
      yPosition += this.disabledData.y[xIndex];
      disabledBar = this.drawBar({
        barWrapper,
        yPosition,
        height: this.disabledData.y[xIndex],
        colors: this.layout.chartColors,
        yIndex: 2,
      });
    }

    return { selectedBar, excludedBar, disabledBar };
  }

  initBars() {
    this.bars = this.chartContent
      .append('g')
      .attr('id', 'bars')
      .attr('class', `bars ${this.hasBarSelection ? '' : 'disable-selection'} ${this.hasTooltip ? 'enable-hover' : ''}`)
      .attr('data-test', 'bars');

    const tickValues = this.layout.xTickValues ? this.layout.xTickValues : this.xScale.ticks();

    for (let i = 0; i < tickValues.length - 1; i++) {
      const lastTickPosition = this.xScale(tickValues[i]);

      const barWidth = (this.xScale(tickValues[i + 1]) - lastTickPosition) / 2;

      const barPosition = lastTickPosition + barWidth / 2;

      const bar = this.bars.append('g');

      const { selectedBar, excludedBar, disabledBar } = this.drawGroupBars(bar, i);

      bar.selectAll('rect').attr('x', barPosition).attr('width', barWidth);

      if (this.hasBarSelection) {
        if (selectedBar) {
          selectedBar.on('click', () => this.onSelectBar(i));
        }

        if (excludedBar) {
          excludedBar.on('click', () => this.onSelectBar(i));
        }
      }

      if (this.hasTooltip) {
        this.attachTooltipEventToBar(selectedBar, this.selectedData?.x[i]);

        this.attachTooltipEventToBar(excludedBar, this.excludedData?.x[i]);

        this.attachTooltipEventToBar(disabledBar, this.disabledData?.x[i]);
      }
    }

    if (this.hasTooltip) {
      this.chartUtility.drawTooltip();

      this.chartContainer.append(this.chartUtility.getTooltip());
    }

    this.isLoading = false;
  }

  initResetFilterButton() {
    if (this.hasBarSelection && this.hasResetFilterButton) {
      this.chartContent
        .append('text')
        .attr('id', 'filterButton')
        .attr('class', 'reset-filter-button')
        .attr('data-test', 'reset-filter-button')
        .attr('x', this.dimensions.width - this.dimensions.axisOffsetRight)
        .attr('y', this.dimensions.axisOffsetTop - 15)
        .attr('text-anchor', 'end')
        .text('reset selection')
        .on('click', () => this.onFilterReset());
    }
  }

  initLayoutData() {
    // Change these values to control margins
    if (!this.dimensions) {
      this.dimensions = {
        marginTop: 60,
        marginBottom: 60,
        marginRight: 60,
        marginLeft: 60,
      };
    }

    // This should not be changed
    this.dimensions = {
      ...this.dimensions,
      width: this.chartContainer.clientWidth - this.dimensions.marginLeft - this.dimensions.marginRight,
      height: this.chartContainer.clientHeight - this.dimensions.marginTop - this.dimensions.marginBottom,
      axisOffsetTop: this.dimensions.marginTop / 2 + this.axisTopOffset,
      axisOffsetBottom: this.axisBottomOffset,
      axisOffsetRight: this.dimensions.marginRight / 2 + this.axisRightOffset,
      axisOffsetLeft: this.dimensions.marginLeft / 2 + this.axisLeftOffset,
    };

    this.chartUtility.setChartDimensions(this.dimensions);

    this.chartWrapper = select(this.chartContainer)
      .append('svg')
      .attr('width', this.dimensions.width + this.dimensions.marginLeft + this.dimensions.marginRight)
      .attr('height', this.dimensions.height + this.dimensions.marginTop + this.dimensions.marginBottom)
      .style('display', 'block');

    const translateX = this.layout.yLabel ? this.dimensions.marginLeft : 0;

    const translateY = this.layout.title ? this.dimensions.marginTop : 0;

    this.chartContent = this.chartWrapper
      // Add main group for drawing chart elements
      .append('g')
      .attr('transform', 'translate(' + translateX + ',' + translateY + ')');

    if (!this.layout.chartColors) {
      this.layout.chartColors = ['blue-500', 'blue-500', 'blue-500'];
    }

    if (!this.layout.xValueRange) {
      this.layout.xValueRange = [].concat(...this.selectedData.x);

      this.layout.xValueRange = [
        Math.trunc(Math.min(...this.layout.xValueRange)),
        Math.ceil(Math.max(...this.layout.xValueRange)),
      ];
    }

    if (!this.layout.yValueRange) {
      this.layout.yValueRange = [Math.trunc(Math.min(...this.selectedData.y)), Math.ceil(Math.max(...this.selectedData.y))];
    }

    const xAxisInfo: AxisInfo = {
      axisDirection: this.xAxisDirection != null ? this.xAxisDirection : XAxisDirection.BOTTOM,
      isAxisInTime: this.isXAxisInTime,
      valueRange: this.layout.xValueRange,
      ticksCount: this.layout.xTickCount,
      tickValues: this.layout.xTickValues,
      tickFormat: (value) => formatTick({ value, unit: this.layout.xUnit, precision: this.layout.xTickValuesPrecision }),
    };

    this.chartUtility.setXAxisInfo(xAxisInfo);

    const yAxisInfo: AxisInfo = {
      axisDirection: this.yAxisDirection != null ? this.yAxisDirection : YAxisDirection.LEFT,
      isAxisInTime: this.isYAxisInTime,
      valueRange: this.layout.yValueRange,
      ticksCount: this.layout.yTickCount,
      tickValues: this.layout.yTickValues,
      tickFormat: (value) => formatTick({ value, unit: this.layout.yUnit, precision: this.layout.yTickValuesPrecision }),
    };

    this.chartUtility.setYAxisInfo(yAxisInfo);
  }

  initChart() {
    if (this.layout) {
      this.clearChart();

      this.initLayoutData();

      this.drawGeneralLayout();

      this.initBars();

      this.initResetFilterButton();
    }
  }

  onSelectBar(xIndex: number) {
    const fromValue = this.layout.xTickValues[xIndex];

    const toValue = this.layout.xTickValues[xIndex + 1];

    this.chartContent.select('#filterButton').classed('active', true);

    this.selectBar.emit([fromValue, toValue]);
  }

  onFilterReset() {
    this.resetFilter.emit();

    this.chartContent.select('#filterButton').classed('active', false);
  }
}
